import React, { useState, useEffect, useCallback, useRef } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Calendar } from "primereact/calendar";
import { SelectButton } from "primereact/selectbutton";
import { Dropdown } from "primereact/dropdown";
import { DataTable, DataTableExpandedRows, DataTableRowEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { TabView, TabPanel } from "primereact/tabview"; // Import TabView components
import { Invoice, InvoiceStatusEnum } from "../../types/invoice.types";
import { useDispatch, useSelector } from "react-redux";
import {
  generateExcelReport,
  getDashboardData,
} from "../../service/report/ReportService";
import { getAllClients } from "../../service/client/ClientServices";
import DatePicker from "react-datepicker";
import {
  ReportSummaryDto,
  MonthlyRevenueDto,
  InvoiceStatusDto,
  TopClientDto,
  TopItemDto,
} from "../../types/report.types";
import { ClientList } from "../../types/client.types";
import moment from "moment";
import { Toast } from "primereact/toast";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { getAllInvoicesByClientId, getInvoiceByClientId } from "../../service/invoice/InvoiceServices";
import { useNavigate } from "react-router-dom";

const LeftIcon = AiOutlineLeft as unknown as React.FC;
const RightIcon = AiOutlineRight as unknown as React.FC;

export const ReportDashboard = () => {
  const dispatch = useDispatch();
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  // State for date selection
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows|undefined>(undefined);
  const [selectedClientId, setSelectedClientId] = useState<any>(null)
  const [totalRecords, setTotalRecords] = useState(0);
  const [dateType, setDateType] = useState<string>("day");
  const [date, setDate] = useState<Date | Date[]>(new Date());
  const [prevDate, setPrevDate] = useState<Date>(new Date());
  const [monthDate, setMonthDate] = useState<Date>(new Date());
  const [monthDateRange, setMonthDateRange] = useState<[Date, Date]>([
    new Date(),
    new Date(),
  ]);
  const [weekDateRange, setWeekDateRange] = useState<[Date, Date]>([
    new Date(),
    new Date(),
  ]);
  const [halfYearlyDateRange, setHalfYearlyDateRange] = useState<[Date, Date]>([
    new Date(),
    new Date(),
  ]);
  const [quarterlyDateRange, setQuarterlyDateRange] = useState<[Date, Date]>([
    new Date(),
    new Date(),
  ]);
  const [financialYearDateRange, setFinancialYearDateRange] = useState<
    [Date, Date]
  >([new Date(), new Date()]);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [disableSelectBtn, setDisableSelectBtn] = useState<boolean>(false);
  const [calender, setCalender] = useState<any>(
    <Calendar value={date as Date} onChange={(e: any) => setDate(e.value)} />
  );
  const [activeTabIndex, setActiveTabIndex] = useState(0); // State for active tab

  // State for filters
  const [client, setClient] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingClients, setIsLoadingClients] = useState<boolean>(false);

  // State for report data
  const [summary, setSummary] = useState<ReportSummaryDto | null>(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenueDto[]>([]);
  const [invoiceStatus, setInvoiceStatus] = useState<InvoiceStatusDto[]>([]);
  const [topClients, setTopClients] = useState<TopClientDto[]>([]);
  // const [topClients, setTopClients] = useState<any[]>([]);
  const [topItems, setTopItems] = useState<TopItemDto[]>([]);
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const localization = useSelector((state: any) => state.localization);


  // Custom Input Component for DatePicker
  const CustomInput = React.forwardRef<HTMLInputElement, any>(
    ({ value, onClick }, ref) => (
      <input
        className="form-control"
        value={value}
        onClick={onClick}
        ref={ref}
        readOnly
        style={{ cursor: "pointer" }}
      />
    )
  );

  // Button options for date selection
  const buttons: any[] = [
    { name: "Day", value: "day" },
    { name: "Week", value: "week" },
    { name: "Month", value: "month" },
    { name: "Half Yearly", value: "halfYearly" },
    { name: "Quarterly", value: "quarterly" },
    { name: "Financial Year", value: "financialYear" },
  ];

  // Utility functions for date calculations
  const getHalfYearDates = (
    selectedDate: Date
  ): { startDate: Date; endDate: Date } => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();

    if (month < 6) {
      // First half (Jan-Jun)
      return {
        startDate: new Date(year, 0, 1), // January 1st
        endDate: new Date(year, 5, 30), // June 30th
      };
    } else {
      // Second half (Jul-Dec)
      return {
        startDate: new Date(year, 6, 1), // July 1st
        endDate: new Date(year, 11, 31), // December 31st
      };
    }
  };

  const getWeekDates = (
    selectedDate: Date
  ): { startDate: Date; endDate: Date } => {
    const dayOfWeek = selectedDate.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6

    // Calculate how many days to subtract to get back to Sunday (start of the week)
    const startDate = new Date(selectedDate);
    startDate.setDate(selectedDate.getDate() - dayOfWeek);

    // End date is 6 days after the start date (Saturday)
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    return {
      startDate,
      endDate,
    };
  };

  const getQuarterlyDates = (
    selectedDate: Date
  ): { startDate: Date; endDate: Date } => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const quarter = Math.floor(month / 3);

    const startMonth = quarter * 3;
    const endMonth = startMonth + 2;
    const endDay = new Date(year, endMonth + 1, 0).getDate(); // Last day of the quarter's last month

    return {
      startDate: new Date(year, startMonth, 1),
      endDate: new Date(year, endMonth, endDay),
    };
  };

  const getFinancialYearDates = (
    selectedDate: Date
  ): { startDate: Date; endDate: Date } => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();

    if (month >= 3) {
      // Financial year starts from April of current year
      return {
        startDate: new Date(year, 3, 1), // April 1st
        endDate: new Date(year + 1, 2, 31), // March 31st next year
      };
    } else {
      // Financial year started from April of previous year
      return {
        startDate: new Date(year - 1, 3, 1), // April 1st previous year
        endDate: new Date(year, 2, 31), // March 31st current year
      };
    }
  };

  const getFirstAndLastDayOfWeek = (selectedDate: Date | Date[]) => {
    if (Array.isArray(selectedDate)) {
      setDate(selectedDate);
      return;
    }
    const weekDays = getWeekDates(selectedDate);
    setWeekDateRange([weekDays.startDate, weekDays.endDate]);
    setDate([weekDays.startDate, weekDays.endDate]);
  };

  const getFirstAndLastDayOfMonth = (selectedDate: Date | Date[]) => {
    if (Array.isArray(selectedDate)) {
      setDate(selectedDate);
      return;
    }

    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth(); // 0 = January, 11 = December

    const startDate = new Date(year, month, 1); // First day of the month
    const endDate = new Date(year, month + 1, 0); // Last day of the month (0th day of next month)

    setMonthDateRange([startDate, endDate]);
    setDate([startDate, endDate]);
  };

  const getNextMonthDates = (
    selectedDate: Date
  ): { startDate: Date; endDate: Date } => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();

    // Move to next month
    const nextMonth = month + 1;
    const startDate = new Date(year, nextMonth, 1);
    const endDate = new Date(year, nextMonth + 1, 0);

    return {
      startDate,
      endDate,
    };
  };

  // Navigation functions
  const handlePrevDate = (type: string, currentDate: Date | Date[]) => {
    const baseDate = Array.isArray(currentDate) ? currentDate[0] : currentDate;

    switch (type) {
      case "day":
        const prevDay = new Date(baseDate);
        prevDay.setDate(prevDay.getDate() - 1);
        setDate(prevDay);
        break;

      case "week":
        const prevWeekStart = new Date(baseDate);
        prevWeekStart.setDate(prevWeekStart.getDate() - 7);

        // Now get the first and last day of that week
        getFirstAndLastDayOfWeek(prevWeekStart);

        break;

      case "month":
        const prevMonth = new Date(
          baseDate.getFullYear(),
          baseDate.getMonth() - 1,
          1
        ); // First day of previous month
        const lastDayOfMonth = new Date(
          baseDate.getFullYear(),
          baseDate.getMonth(),
          0
        ); // First day of previous month
        setMonthDateRange([prevMonth, lastDayOfMonth]);
        setDate(prevMonth);
        break;

      case "halfYearly":
        const prevHalfYear = new Date(baseDate);
        prevHalfYear.setMonth(prevHalfYear.getMonth() - 6);
        const prevHalfYearDates = getHalfYearDates(prevHalfYear);
        setHalfYearlyDateRange([
          prevHalfYearDates.startDate,
          prevHalfYearDates.endDate,
        ]);
        setDate(prevHalfYear);
        break;

      case "quarterly":
        const prevQuarter = new Date(baseDate);
        prevQuarter.setMonth(prevQuarter.getMonth() - 3);
        const prevQuarterDates = getQuarterlyDates(prevQuarter);
        setQuarterlyDateRange([
          prevQuarterDates.startDate,
          prevQuarterDates.endDate,
        ]);
        setDate(prevQuarter);
        break;

      case "financialYear":
        const prevFinYear = new Date(baseDate);
        prevFinYear.setFullYear(prevFinYear.getFullYear() - 1);
        const prevFinYearDates = getFinancialYearDates(prevFinYear);
        setFinancialYearDateRange([
          prevFinYearDates.startDate,
          prevFinYearDates.endDate,
        ]);
        setDate(prevFinYear);
        break;
    }
  };

  const handleNextDate = (type: string, currentDate: Date | Date[]) => {
    const baseDate = Array.isArray(currentDate) ? currentDate[0] : currentDate;

    switch (type) {
      case "day":
        const nextDay = new Date(baseDate);
        nextDay.setDate(nextDay.getDate() + 1);
        setDate(nextDay);
        break;

      case "week":
        const nextWeek = new Date(baseDate);
        nextWeek.setDate(nextWeek.getDate() + 7);
        getFirstAndLastDayOfWeek(nextWeek);
        break;

      case "month":
        const nextMonthRange = getNextMonthDates(baseDate);
        setMonthDateRange([nextMonthRange.startDate, nextMonthRange.endDate]);
        setDate(nextMonthRange.startDate);
        break;

      case "halfYearly":
        const nextHalfYear = new Date(baseDate);
        nextHalfYear.setMonth(nextHalfYear.getMonth() + 6);
        const nextHalfYearDates = getHalfYearDates(nextHalfYear);
        setHalfYearlyDateRange([
          nextHalfYearDates.startDate,
          nextHalfYearDates.endDate,
        ]);
        setDate(nextHalfYear);
        break;

      case "quarterly":
        const nextQuarter = new Date(baseDate);
        nextQuarter.setMonth(nextQuarter.getMonth() + 3);
        const nextQuarterDates = getQuarterlyDates(nextQuarter);
        setQuarterlyDateRange([
          nextQuarterDates.startDate,
          nextQuarterDates.endDate,
        ]);
        setDate(nextQuarter);
        break;

      case "financialYear":
        const nextFinYear = new Date(baseDate);
        nextFinYear.setFullYear(nextFinYear.getFullYear() + 1);
        const nextFinYearDates = getFinancialYearDates(nextFinYear);
        setFinancialYearDateRange([
          nextFinYearDates.startDate,
          nextFinYearDates.endDate,
        ]);
        setDate(nextFinYear);
        break;
    }
  };
  // Handle date type change
  const handleDate = (e: any) => {
    // Prevent deselection on re-click
    if (!e.value || e.value === dateType) return;
    setDateType(e.value);
    setShowDatePicker(false);

    // Initialize date ranges based on current date
    const currentDate = new Date();

    switch (e.value) {
      case "halfYearly":
        const halfYearDates = getHalfYearDates(currentDate);
        setHalfYearlyDateRange([
          halfYearDates.startDate,
          halfYearDates.endDate,
        ]);
        setDate(currentDate);
        break;

      case "quarterly":
        const quarterDates = getQuarterlyDates(currentDate);
        setQuarterlyDateRange([quarterDates.startDate, quarterDates.endDate]);
        setDate(currentDate);
        break;

      case "financialYear":
        const finYearDates = getFinancialYearDates(currentDate);
        setFinancialYearDateRange([
          finYearDates.startDate,
          finYearDates.endDate,
        ]);
        setDate(currentDate);
        break;

      case "week":
        const weekDays = getWeekDates(currentDate);
        setWeekDateRange([weekDays.startDate, weekDays.endDate]);
        setDate(currentDate);
        break;

      case "month":
        const startOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1
        );
        const endOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        ); // today's date
        setMonthDateRange([startOfMonth, endOfMonth]);
        setDate(startOfMonth);
        break;

      default:
        setDate(currentDate);
    }
  };

  // Handle calendar rendering
  const handleCalender = () => {
    if (dateType === "day") {
      setCalender(
        <DatePicker
          selected={date as Date}
          onChange={(e: any) => {
            setDate(e as Date);
          }}
        />
      );
      setPrevDate(date as Date);
    } else if (dateType === "month") {
      setCalender(
        <DatePicker
          selected={monthDateRange[0]}
          customInput={<CustomInput />}
          startDate={monthDateRange[0]}
          endDate={monthDateRange[1]}
          onChange={(e: any) => {
            getFirstAndLastDayOfMonth(e);
            setDate(e);
          }}
          dateFormat="MMMM yyyy"
          showMonthYearPicker
        />
      );
      setMonthDate(date as Date);
    } else if (dateType === "week") {
      const weekDates = Array.isArray(date) ? date : [date, date];
      setCalender(
        <DatePicker
          selectsRange={true}
          customInput={<CustomInput />}
          startDate={weekDateRange[0]}
          endDate={weekDateRange[1]}
          onChange={(range: [Date, Date]) => getFirstAndLastDayOfWeek(range[0])}
          dateFormatCalendar="MMMM yyyy"
        />
      );
    } else if (dateType === "halfYearly") {
      setCalender(
        <DatePicker
          selectsRange
          customInput={<CustomInput />}
          startDate={halfYearlyDateRange[0]}
          endDate={halfYearlyDateRange[1]}
          onChange={(range: [Date, Date]) => {
            if (range[0] || range[1]) {
              const updatedHalfYear = getHalfYearDates(range[0]);
              setHalfYearlyDateRange([
                updatedHalfYear.startDate,
                updatedHalfYear.endDate,
              ]);
              setDate(range[0]);
            }
          }}
          dateFormat="MMMM yyyy"
        />
      );
    } else if (dateType === "quarterly") {
      setCalender(
        <DatePicker
          selectsRange
          customInput={<CustomInput />}
          startDate={quarterlyDateRange[0]}
          endDate={quarterlyDateRange[1]}
          onChange={(range: [Date, Date]) => {
            if (range[0] || range[1]) {
              const updatedQuarter = getQuarterlyDates(range[0]);
              setQuarterlyDateRange([
                updatedQuarter.startDate,
                updatedQuarter.endDate,
              ]);
              setDate(range[0]);
            }
          }}
          dateFormatCalendar="MMMM yyyy"
        />
      );
    } else if (dateType === "financialYear") {
      setCalender(
        <DatePicker
          selectsRange
          customInput={<CustomInput />}
          startDate={financialYearDateRange[0]}
          endDate={financialYearDateRange[1]}
          onChange={(range: [Date, Date]) => {
            if (range[0] || range[1]) {
              const updatedFinancialYear = getFinancialYearDates(range[0]);
              setFinancialYearDateRange([
                updatedFinancialYear.startDate,
                updatedFinancialYear.endDate,
              ]);
              setDate(range[0]);
            }
          }}
          dateFormat="MMMM yyyy"
        />
      );
    }
  };

  // Function to format dates to DD/MM/YYYY
  const formatDate = (date: Date): string => {
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  // Get current date range based on selected type
  const getCurrentDateRange = (): [Date, Date] => {
    switch (dateType) {
      case "month":
        return monthDateRange;
      case "halfYearly":
        return halfYearlyDateRange;
      case "quarterly":
        return quarterlyDateRange;
      case "financialYear":
        return financialYearDateRange;
      case "week":
        return weekDateRange;
      default:
        const singleDate = Array.isArray(date) ? date[0] : (date as Date);
        return [singleDate, singleDate];
    }
  };

  // Excel download function
  function downloadExcelFromBlob(blob: Blob, fileName: string = "report.xlsx") {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  const handleExcelDownload = async () => {
    setIsLoading(true);
    try {
      const [startDate, endDate] = getCurrentDateRange();

      const request = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        clientId: client?.code !== "ALL" ? parseInt(client?.code) : undefined,
      };

      const blob = await generateExcelReport(
        dispatch,
        {
          ...request,
          clientName: client.name,
        },
        { responseType: "blob" }
      );

      downloadExcelFromBlob(blob, `Report_${client.name}.xlsx`);
    } catch (error) {
      console.error("Failed to download Excel:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    const [startDate, endDate] = getCurrentDateRange();

    setIsLoading(true);
    try {
      const request = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        clientId: client?.code !== "ALL" ? parseInt(client?.code) : undefined,
      };

      const response = await getDashboardData(dispatch, request);
      if (response) {
        setSummary(response.summary);
        setMonthlyRevenue(response.monthlyRevenue);
        setInvoiceStatus(
          response?.invoiceStatus?.map((status: any) => ({
            ...status,
            color:
              status.name === "Paid"
                ? "#10b981"
                : status.name === "Pending"
                  ? "#FEBA17"
                  : status.name === "Partial"
                    ? "#C562AF"
                    : "#0ABAB5",
          }))
        );
        setTopClients(response.topClients);
        setTopItems(response.topItems);
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    dateType,
    date,
    halfYearlyDateRange,
    quarterlyDateRange,
    financialYearDateRange,
    client,
    dispatch,
  ]);

  // Function to fetch clients
  const fetchClients = useCallback(async () => {
    setIsLoadingClients(true);
    try {
      const response = await getAllClients(dispatch, {
        Page: 1,
        Limit: 100,
        SortProperty: "client_name",
        SortBy: "ASC",
      });

      if (response) {
        const options = [
          ...response?.List?.map((client: ClientList) => ({
            name: client.client_name,
            code: client.client_id.toString(),
            id: client.client_id,
          })),
        ];
        setClientOptions(options);
      }
    } catch (error) {
      console.error("Error loading clients:", error);
    } finally {
      setIsLoadingClients(false);
    }
  }, [dispatch]);

  // Toolbar component
  const startToolbar = () => {
    return (
      <div className="flex flex-col gap-2 align-center report-start-tool">
        <SelectButton
          value={dateType}
          onChange={(e) => handleDate(e)}
          optionLabel="name"
          options={buttons}
          disabled={disableSelectBtn}
        />
      </div>
    );
  };

  // Effects
  useEffect(() => {
    handleCalender();
  }, [
    dateType,
    date,
    halfYearlyDateRange,
    quarterlyDateRange,
    financialYearDateRange,
  ]);

  useEffect(() => {
    loadDashboardData();
  }, [
    client,
    dateType,
    date,
    halfYearlyDateRange,
    quarterlyDateRange,
    financialYearDateRange,
  ]);

  useEffect(() => {
    fetchClients();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const calculateChange = (
    current: number,
    previous?: number
  ): { value: string; isPositive: boolean } => {
    if (!previous) return { value: "N/A", isPositive: true };

    const change = ((current - previous) / previous) * 100;
    return {
      value: `${Math.abs(change).toFixed(1)}%`,
      isPositive: change >= 0,
    };
  };

  const allowExpansion = (rowData: any) => {
    // setSelectedClient(rowData.clientId)
    return rowData.clientName?.length > 0;
  };

  // Column Templates
  const invoiceNumberTemplate = (rowData: any) => {
    return (
      <a
        href={`/invoice/${rowData.id}`}
        className="text-blue-600 hover:underline font-medium"
        onClick={(e) => {
          e.preventDefault();
          navigate(`/invoice/${rowData?.invoice_id}`);
        }}
      >
        {rowData?.invoice_number}
      </a>
    );
  };

  const statusTemplate = (rowData: Invoice) => {
    return <StatusBadge status={rowData.status} />;
  };

  const amountTemplate = (rowData: Invoice) => {
      const totalNumber = Number(rowData.total);
  const formattedTotal = isNaN(totalNumber) ? '0.00' : totalNumber.toFixed(2);
    return (
      <span className="font-medium">
        {rowData.currency_symbol}
        {formattedTotal}
      </span>
    );
  };

  const dateTemplate = (rowData: Invoice) => {
    return moment(rowData.issue_date).format("DD/MM/YY");
  };

  const dueDateTemplate = (rowData: Invoice) => {
    return moment(rowData?.due_date).format("DD/MM/YY");
  };

  const clientTemplate = (rowData: any) => {
    return <div>{rowData?.client_name}</div>;
  };


  const getAllInvoiceByClientId = async () => {
    const [startDate, endDate] = getCurrentDateRange();
    const input = {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      clientId:selectedClientId
    }
    const invoicesByClients = await getAllInvoicesByClientId(dispatch,input);
    if (invoicesByClients) {

      const tempTopClient = [...topClients]
      const clientIndex = tempTopClient.findIndex((client) => { return client.clientId === selectedClientId })
      tempTopClient[clientIndex] = {
        ...tempTopClient[clientIndex],
        invoice: invoicesByClients
      }
      setTopClients(tempTopClient)

    }
  }

  useEffect(() => {
    if (selectedClientId) {
      getAllInvoiceByClientId()
    }

  }, [selectedClientId])
 useEffect(() => {
    // reset expanded rows and table data
    setExpandedRows(undefined);
    setSelectedClientId(null);
    setTopClients([]);
  }, [client]);

  const rowExpansionTemplate = (data: any) => {
    return (
      <div className="p-3">
        <h5>Invoices of {data.clientName}</h5>
        <DataTable
          value={data.invoice}
          lazy
          emptyMessage={localization?.NoInvoicesFound || "No invoices found"}
          responsiveLayout="scroll"
          totalRecords={totalRecords}
          stripedRows
          className="p-datatable-sm"
          rowHover
          size="small"
          style={{ maxHeight: '250px', overflow: 'auto' }}
        >
          <Column
            field="invoice_number"
            header={localization?.Invoice || "Invoice"}
            body={invoiceNumberTemplate}
            sortable
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
          />
          <Column
            field="client"
            header={localization?.Client || "Client"}
            sortable
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
            body={clientTemplate}
          />
          <Column
            field="issueDate"
            header={localization?.IssueDate || "Issue Date"}
            body={dateTemplate}
            sortable
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
          />
          <Column
            field="dueDate"
            header={localization?.DueDate || "Due Date"}
            body={dueDateTemplate}
            sortable
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
          />
          <Column
            field="status"
            header={localization?.Status || "Status"}
            body={statusTemplate}
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
          />
          <Column
            field="total"
            header={localization?.Amount || "Amount"}
            body={amountTemplate}
            sortable
            style={{ textAlign: "left" }}
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
          />
        </DataTable>
      </div>
    );
  };


  const onRowExpand = (event: DataTableRowEvent) => {
    setSelectedClientId(event.data.clientId);
  };


  useEffect(() => {
    setExpandedRows(undefined); // or setExpandedRows({})
    setSelectedClientId(null);
  }, [date]);


  return (
    <div className="space-y-6">
      <Toast ref={toast} />
      <h1 className="text-xl font-bold surface-900 mb-4">Reports</h1>

      {/* Filters */}
      <Card className="p-0 card_content_padding">
        <div className="flex flex-wrap justify-between items-center p-0 gap-3">
          <div className="flex flex-wrap gap-4 align-items-center">
            {startToolbar()}
            {/* Date input with navigation */}
            <div className="flex relative report_date_picker">
              <Button
                onClick={() => handlePrevDate(dateType, date)}
                className="date-comp-prev-btn"
                disabled={disableSelectBtn}
              >
                {<LeftIcon />}
                {/* left */}
              </Button>
              {calender}

              <Button
                onClick={() => handleNextDate(dateType, date)}
                className="date-comp-next-btn"
                disabled={disableSelectBtn}
              >
                <RightIcon />
              </Button>
            </div>
            <div className="flex flex-col justify-start">
              <Dropdown
                value={client}
                options={clientOptions}
                onChange={(e) => setClient(e.value)}
                optionLabel="name"
                className="w-48 h-[45px]"
                placeholder="Select a Client"
                loading={isLoadingClients}
                filter
                showFilterClear
                emptyFilterMessage="No clients found"
                emptyMessage="No clients available"
              />
            </div>
          </div>
          <Button
            variant="primary"
            className="h-[45px]"
            onClick={() => {
              loadDashboardData();
              handleExcelDownload();
            }}
            isLoading={isLoading}
            disabled={!client || isLoading}
          >
            Generate Report
          </Button>
        </div>
      </Card>

      {/* Tables with TabView */}
      <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>
        <TabPanel header="Clients">
          <Card className="card_content_padding">
            <div className="p-0">
              <h2 className="text-gray-800 text-lg font-semibold mb-4 mt-1">
                Top Clients by Revenue
              </h2>
              <DataTable
                value={topClients} 
                expandedRows={expandedRows} 
                onRowToggle={(e: any) => setExpandedRows(e.data)}
                onRowExpand={onRowExpand} 
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="clientName" 
                tableStyle={{ minWidth: '60rem' }}
                loading={isLoading}
                emptyMessage="No client data available"
              >
                <Column expander={allowExpansion} style={{ width: '5rem' }} />
                <Column
                  field="clientName"
                  header="Client Name"
                  style={{ fontWeight: 500 }}
                ></Column>
                <Column field="invoices" header="Invoices"></Column>
                <Column
                  field="revenue"
                  header="Revenue"
                  body={(rowData) => formatCurrency(rowData.revenue)}
                ></Column>
                <Column
                  field="outstanding"
                  header="Outstanding"
                  body={(rowData) => formatCurrency(rowData.outstanding)}
                ></Column>
              </DataTable>
            </div>
          </Card>
        </TabPanel>
        <TabPanel header="Items">
          <Card className="card_content_padding">
            <div className="p-0">
              <h2 className="text-gray-800 text-lg font-semibold mb-4 mt-1">
                Top Items/Services
              </h2>
              <DataTable
                value={topItems}
                stripedRows
                showGridlines
                size="small"
                tableStyle={{ minWidth: "50rem" }}
                loading={isLoading}
                emptyMessage="No item data available"
              >
                <Column
                  field="name"
                  header="Item/Service"
                  style={{ fontWeight: 500 }}
                ></Column>
                <Column field="quantity" header="Quantity"></Column>
                <Column
                  field="revenue"
                  header="Revenue"
                  body={(rowData) => formatCurrency(rowData.revenue)}
                ></Column>
                <Column
                  field="avgPrice"
                  header="Avg. Price"
                  body={(rowData) => formatCurrency(rowData.avgPrice)}
                ></Column>
              </DataTable>
            </div>
          </Card>
        </TabPanel>
      </TabView>
    </div>
  );
};